import {
  parseAssetProcessingActivityDetailForFE,
  parseOwnerAssetProcessingActivityReviewMetaForFE,
} from '../assetProcessingActivities/assetProcessingActivities.parsers';

import type {
  ActiveRopaReviewMetrics,
  ActiveRopaReviewMetricsDTO,
  ActiveRopaReviewPanel,
  ActiveRopaReviewPanelActions,
  ActiveRopaReviewPanelActionsDTO,
  ActiveRopaReviewPanelDTO,
  ActiveRopaReviewResponse,
  ActiveRopaReviewResponseDTO,
  AcceptProcessingReviewInput,
  AdminAssetProcessingActivityReviewDetail,
  AdminAssetProcessingActivityReviewDetailDTO,
  AdminAssetProcessingActivityReviewDetailRow,
  AdminAssetProcessingActivityReviewDetailRowDTO,
  AdminAssetProcessingActivityReviewRowDTO,
  AdminAssetRopaReviewListDTO,
  AdminReviewDecisionActions,
  AdminReviewDecisionActionsDTO,
  AssetProcessingActivityReviewActionOutputDTO,
  AssetProcessingActivityReviewRow,
  AssetRopaReview,
  AssetRopaReviewListParams,
  AssetRopaReviewListParamsInput,
  AssetRopaReviewListParamsLegacy,
  AssetRopaWarningFlags,
  AssetRopaWarningFlagsDTO,
  RopaReviewRound,
  RopaReviewRoundOutputDTO,
  RopaReviewStartSummary,
  RopaReviewStartSummaryDTO,
  RequestClarificationInput,
} from './ropaReviews.types';

export const parseAssetRopaWarningFlagsForFE = (dto: AssetRopaWarningFlagsDTO): AssetRopaWarningFlags => ({
  dpiaIndicated: dto.dpia_indicated,
  dpiaRequiredUnapproved: dto.dpia_required_unapproved,
  thirdCountryProcessing: dto.third_country_processing,
  sensitivePersonalData: dto.sensitive_personal_data,
  confirmedNoProcessingActivities: dto.confirmed_no_processing_activities,
});

const parseMenuActions = (menuActions: Record<string, boolean> | undefined): Record<string, boolean> => {
  if (!menuActions || typeof menuActions !== 'object') {
    return {};
  }

  return Object.entries(menuActions).reduce<Record<string, boolean>>((accumulator, [key, value]) => {
    accumulator[key] = Boolean(value);
    return accumulator;
  }, {});
};

export const parseAssetProcessingActivityReviewRowForFE = (
  dto: AdminAssetProcessingActivityReviewRowDTO,
): AssetProcessingActivityReviewRow => ({
  id: dto.id,
  assetProcessingActivityId: dto.asset_processing_activity_id,
  processingActivityId: dto.processing_activity_id,
  processingActivityName: dto.processing_activity_name,
  reviewStatus: dto.review_status,
  activePurposesCount: dto.active_purposes_count,
  dpiaDisplayStatus: dto.dpia_display_status,
  dpiaId: dto.dpia_id ?? null,
  updatedAt: dto.updated_at,
  canAccept: Boolean(dto.can_accept),
  canRequestClarification: Boolean(dto.can_request_clarification),
  // BE may omit can_open; default to open so row click matches Processing Activities tables.
  canOpen: dto.can_open !== false,
});

export const parseAssetProcessingActivityReviewActionForFE = (
  dto: AssetProcessingActivityReviewActionOutputDTO,
): AssetProcessingActivityReviewActionOutputDTO => dto;

export const parseAssetRopaReviewForFE = (dto: AdminAssetRopaReviewListDTO): AssetRopaReview => ({
  id: dto.id,
  assetRopaId: dto.asset_ropa_id,
  assetId: dto.asset_id,
  assetName: dto.asset_name,
  assetTypeId: dto.asset_type_id,
  assetTypeName: dto.asset_type_name,
  operationalOwnerMembershipId: dto.operational_owner_membership_id,
  operationalOwnerName: dto.operational_owner_name,
  processingActivitiesCount: dto.processing_activities_count,
  permanentRopaStatus: dto.permanent_ropa_status,
  reviewStatus: dto.review_status,
  warnings: parseAssetRopaWarningFlagsForFE(dto.warnings),
  processingActivityReviews: (dto.processing_activity_reviews ?? []).map(parseAssetProcessingActivityReviewRowForFE),
  menuActions: parseMenuActions(dto.menu_actions),
});

export const parseAssetRopaReviewsForFE = (dtos: AdminAssetRopaReviewListDTO[]): AssetRopaReview[] => dtos.map(parseAssetRopaReviewForFE);

export const parseActiveRopaReviewMetricsForFE = (dto: ActiveRopaReviewMetricsDTO): ActiveRopaReviewMetrics => ({
  includedAssetsCount: dto.included_assets_count,
  awaitingResponseCount: dto.awaiting_response_count,
  submittedCount: dto.submitted_count,
  acceptedCount: dto.accepted_count,
  clarificationRequestedCount: dto.clarification_requested_count,
  assetsWithWarningsCount: dto.assets_with_warnings_count,
});

export const parseActiveRopaReviewPanelForFE = (dto: ActiveRopaReviewPanelDTO): ActiveRopaReviewPanel => ({
  id: dto.id,
  status: dto.status,
  initiatedAt: dto.initiated_at,
  deadline: dto.deadline,
  reviewInstructions: dto.review_instructions,
  initiatedByMembershipId: dto.initiated_by_membership_id,
  completedAt: dto.completed_at ?? null,
  cancelledAt: dto.cancelled_at ?? null,
  completionComment: dto.completion_comment,
  cancellationComment: dto.cancellation_comment,
  completedByMembershipId: dto.completed_by_membership_id ?? null,
  cancelledByMembershipId: dto.cancelled_by_membership_id ?? null,
});

export const parseActiveRopaReviewPanelActionsForFE = (dto: ActiveRopaReviewPanelActionsDTO): ActiveRopaReviewPanelActions => ({
  canEditDetails: dto.can_edit_details,
  canCompleteReview: dto.can_complete_review,
  canCancelReview: dto.can_cancel_review,
});

export const parseActiveRopaReviewResponseForFE = (dto: ActiveRopaReviewResponseDTO): ActiveRopaReviewResponse => ({
  review: parseActiveRopaReviewPanelForFE(dto.review),
  metrics: parseActiveRopaReviewMetricsForFE(dto.metrics),
  actions: parseActiveRopaReviewPanelActionsForFE(dto.actions),
});

export const parseRopaReviewStartSummaryForFE = (dto: RopaReviewStartSummaryDTO): RopaReviewStartSummary => ({
  eligibleAssetsCount: dto.eligible_assets_count,
  uniqueOperationalOwnersCount: dto.unique_operational_owners_count,
  assetProcessingActivitiesCount: dto.asset_processing_activities_count,
});

export const parseRopaReviewRoundForFE = (dto: RopaReviewRoundOutputDTO): RopaReviewRound => ({
  id: dto.id,
  status: dto.status,
  initiatedAt: dto.initiated_at,
  deadline: dto.deadline,
  reviewInstructions: dto.review_instructions,
  initiatedByMembershipId: dto.initiated_by_membership_id,
  eligibleAssetsCount: dto.eligible_assets_count,
  assetRopaReviewsCount: dto.asset_ropa_reviews_count,
  assetProcessingActivityReviewsCount: dto.asset_processing_activity_reviews_count,
});

export const parseRopaReviewDetailsUpdateForBE = (payload: { deadline?: string; reviewInstructions?: string }) => ({
  ...(payload.deadline !== undefined ? { deadline: payload.deadline } : {}),
  ...(payload.reviewInstructions !== undefined ? { review_instructions: payload.reviewInstructions } : {}),
});

export const parseRopaReviewCreateForBE = (payload: { deadline: string; reviewInstructions: string }) => ({
  deadline: payload.deadline,
  review_instructions: payload.reviewInstructions,
});

const isLegacyAssetRopaReviewListParams = (params: AssetRopaReviewListParamsInput): params is AssetRopaReviewListParamsLegacy =>
  'asset_type_id' in params ||
  'operational_owner_membership_id' in params ||
  'warning_state' in params ||
  'sensitive_personal_data' in params ||
  'third_country_processing' in params;

const mapLegacyDpiaState = (value: string): string | undefined => {
  if (value === 'required_unapproved') {
    return 'dpia_required';
  }

  if (value === 'none') {
    return 'not_indicated';
  }

  return value;
};

export const parseAssetRopaReviewListParamsForBE = (params?: AssetRopaReviewListParamsInput): AssetRopaReviewListParams | undefined => {
  if (!params) {
    return undefined;
  }

  if (!isLegacyAssetRopaReviewListParams(params)) {
    return params;
  }

  const legacy = params;

  return {
    ...(legacy.search ? { search: legacy.search } : {}),
    ...(legacy.review_status ? { review_status: legacy.review_status } : {}),
    ...(legacy.asset_type_id != null ? { asset_type: legacy.asset_type_id } : {}),
    ...(legacy.operational_owner_membership_id != null ? { operational_owner: legacy.operational_owner_membership_id } : {}),
    ...(legacy.warning_state === 'with_warnings' ? { has_warnings: true } : {}),
    ...(legacy.warning_state === 'no_warnings' ? { has_warnings: false } : {}),
    ...(legacy.dpia_state ? { dpia_state: mapLegacyDpiaState(legacy.dpia_state) } : {}),
    ...(legacy.sensitive_personal_data !== undefined ? { sensitive_data: legacy.sensitive_personal_data } : {}),
  };
};

export const parseRopaReviewCompleteForBE = (payload: { completionComment: string }) => ({
  completion_comment: payload.completionComment,
});

export const parseRopaReviewCancelForBE = (payload: { cancellationComment: string }) => ({
  cancellation_comment: payload.cancellationComment,
});

export const parseAcceptProcessingReviewForBE = (payload: AcceptProcessingReviewInput = {}) => {
  const comment = payload.comment?.trim();

  if (!comment) {
    return {};
  }

  return {
    comment,
    review_decision_comment: comment,
  };
};

export const parseCancelIncompleteAssetReviewForBE = (payload: { cancellationComment: string }) => ({
  cancellation_comment: payload.cancellationComment.trim(),
});

export const parseRequestClarificationForBE = (payload: RequestClarificationInput) => ({
  comment: payload.comment,
});

export const parseAdminReviewDecisionActionsForFE = (dto: AdminReviewDecisionActionsDTO): AdminReviewDecisionActions => ({
  canAccept: dto.can_accept,
  canRequestClarification: dto.can_request_clarification,
});

export const parseAdminAssetProcessingActivityReviewDetailRowForFE = (
  dto: AdminAssetProcessingActivityReviewDetailRowDTO,
): AdminAssetProcessingActivityReviewDetailRow => ({
  id: dto.id,
  reviewRoundId: dto.review_round_id,
  status: dto.status,
  submittedAt: dto.submitted_at,
  submittedByMembershipId: dto.submitted_by_membership_id,
  submittedByName: dto.submitted_by_name ?? null,
  submittedRevision: dto.submitted_revision,
  acceptedAt: dto.accepted_at,
  acceptedByMembershipId: dto.accepted_by_membership_id,
  clarificationComment: dto.clarification_comment,
  clarificationRequestedAt: dto.clarification_requested_at,
  clarificationRequestedByMembershipId: dto.clarification_requested_by_membership_id,
});

export const parseAdminAssetProcessingActivityReviewDetailForFE = (
  dto: AdminAssetProcessingActivityReviewDetailDTO,
): AdminAssetProcessingActivityReviewDetail => ({
  ...parseAssetProcessingActivityDetailForFE(dto),
  review: parseOwnerAssetProcessingActivityReviewMetaForFE(dto.review),
  reviewRow: parseAdminAssetProcessingActivityReviewDetailRowForFE(dto.review_row),
  actions: parseAdminReviewDecisionActionsForFE(dto.actions),
  dpiaId: dto.dpia_id ?? null,
});

/** @deprecated Use parseAdminAssetProcessingActivityReviewDetailForFE */
export const parseProcessingReviewDetailForFE = parseAdminAssetProcessingActivityReviewDetailForFE;
